<template>
  <div v-if="ad" class="ad-banner" :style="{ height: computedHeight }">
    <div class="ad-card">
      <a :href="adLink" @click.prevent="onClick">
        <img :src="adImage" :alt="ad.title || '广告'" loading="eager" decoding="async" fetchpriority="high" @error="handleImageError" />
        <div v-if="showTitle && ad.title" class="ad-title" :title="ad.title">{{ ad.title }}</div>
      </a>
    </div>
  </div>
</template>

<script>
import api from '@/utils/request'
import { processImageUrl } from '@/utils'

export default {
  name: 'AdBanner',
  props: {
    position: { type: String, required: true },
    height: { type: [Number, String], default: 300 },
    showTitle: { type: Boolean, default: true }
  },
  data() {
    return {
      ad: null
    }
  },
  computed: {
    computedHeight() {
      return typeof this.height === 'number' ? `${this.height}px` : this.height
    },
    adLink() {
      if (!this.ad) return '#'
      const t = this.ad.link_type
      if (t === 'external') return this.ad.external_link || '#'
      if (t === 'product' && this.ad.link_id) return `/products/${this.ad.link_id}`
      if (t === 'enterprise' && this.ad.link_id) return `/enterprise/${this.ad.link_id}`
      if (t === 'demand' && this.ad.link_id) return `/demands/${this.ad.link_id}`
      if (t === 'expert' && this.ad.link_id) return `/expert/${this.ad.link_id}`
      return '#'
    },
    adImage() {
      if (!this.ad) return ''
      return processImageUrl(this.ad.image_url) || this.defaultImage()
    }
  },
  methods: {
    getCacheKey() {
      return `pc:ad-banner:${this.position}`
    },
    saveAdToCache(ad) {
      if (!ad) return
      try {
        sessionStorage.setItem(this.getCacheKey(), JSON.stringify(ad))
      } catch (e) {}
    },
    clearAdCache() {
      try {
        sessionStorage.removeItem(this.getCacheKey())
      } catch (e) {}
    },
    preloadImage(url) {
      if (!url) return
      try {
        const img = new Image()
        img.decoding = 'async'
        img.src = url
      } catch (e) {}
    },
    async loadAd() {
      try {
        const list = await api({ url: `/advertisements/positions/${this.position}`, method: 'get' })
        if (Array.isArray(list) && list.length) {
          this.ad = list[0]
          this.saveAdToCache(this.ad)
          this.preloadImage(this.adImage)
        } else {
          this.ad = null
          this.clearAdCache()
        }
      } catch (e) {
        this.ad = null
        this.clearAdCache()
      } finally {
        this.$emit('load', !!this.ad)
      }
    },
    handleImageError(event) {
      event.target.src = this.defaultImage()
      event.target.onerror = null
    },
    onClick() {
      if (!this.ad) return
      api({ url: `/advertisements/${this.ad.id}/click`, method: 'post' }).catch(() => {})
      const link = this.adLink
      if (!link || link === '#') return
      if (this.ad.link_type === 'external') window.open(link, '_blank')
      else this.$router.push(link)
    },
    defaultImage() {
      return processImageUrl('/uploads/default/ad.png')
    }
  },
  mounted() {
    this.loadAd()
  },
  watch: {
    position() {
      this.ad = null
      this.loadAd()
    }
  }
}
</script>

<style scoped>
.ad-banner { width: 100%; }
.ad-card {
  height: 100%;
  background: #f8f9fa;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px; /* 添加内边距，防止图片贴边 */
}
.ad-card a { 
  display: flex; 
  width: 100%; 
  height: 100%; 
  position: relative;
  align-items: center;
  justify-content: center;
}
.ad-card img { 
  width: 100%; 
  height: 100%; 
  object-fit: contain; /* 改为contain，完整显示图片 */
  object-position: center; /* 居中显示 */
  display: block; 
}
.ad-title {
  position: absolute; left: 0; right: 0; bottom: 0;
  padding: 6px 10px; font-size: 12px; color: #fff;
  background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%);
  white-space: normal; /* 允许换行，避免截断 */
  overflow: visible; /* 允许内容完全显示 */
  text-overflow: initial; /* 移除省略号 */
  word-wrap: break-word; /* 长单词换行 */
  line-height: 1.2; /* 紧凑行高 */
}
.ad-placeholder {
  height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center;
  color: #909399; border: 2px dashed #dcdfe6; border-radius: 12px; background: #fff;
}

.ad-placeholder p {
  text-align: center;
  word-wrap: break-word; /* 允许长文本换行 */
  white-space: normal; /* 允许换行 */
  max-width: 100%; /* 确保不超出容器 */
  margin: 0;
  padding: 0 10px; /* 添加内边距，确保文字不贴边 */
  box-sizing: border-box; /* 确保内边距计算在总宽度内 */
}
</style>




